import React, { type HTMLAttributes, useEffect, useMemo } from 'react';

import { Link } from 'react-router-dom';

import { CardChainIcon } from '@pkg/components';
import { type SupportedChain } from '@pkg/types';
import { trimDecimals } from '@pkg/utils';

import { Typography } from '../../../DesignSystem';

import {
    StyledChip,
    StyledEthIcon,
    StyledSearchListItemSpacer,
    StyledSearchListItemWrapper,
    StyledThumbnail,
    StyledTypography,
} from './styles';

interface CollectionItemGlobalSearchProps extends HTMLAttributes<HTMLDivElement> {
    address: string;
    name: string;
    thumbnail: string;
    slug: string;
    floor_price?: string;
    focused?: boolean;
    chain: SupportedChain;
    onFocused?: (link: string) => void;
}

export function CollectionItem({
    name,
    thumbnail,
    slug,
    floor_price,
    chain,
    focused,
    onFocused,
    ...rest
}: CollectionItemGlobalSearchProps) {
    const link = `/collection/${chain}/${slug}`;

    useEffect(() => {
        focused && onFocused?.(link);
    }, [focused, link, onFocused]);

    const priceValue = useMemo(() => {
        const floor = Number(floor_price);

        if (floor < 1) {
            return trimDecimals(floor, 4);
        } else if (floor < 1000) {
            return trimDecimals(floor);
        } else if (floor < 1000000) {
            return `${trimDecimals(floor / 1000)}k`;
        }

        return `${trimDecimals(floor / 1000000)}m`;
    }, [floor_price]);

    return (
        <Link to={link}>
            <StyledSearchListItemWrapper {...rest} $focused={focused}>
                <StyledThumbnail src={thumbnail} />
                <StyledTypography title={name} variant="h3" type="label" size={14} weight="700">
                    {name}
                </StyledTypography>
                <StyledSearchListItemSpacer />
                {floor_price ? (
                    <StyledChip>
                        <StyledEthIcon>
                            <CardChainIcon chain={chain} />
                        </StyledEthIcon>
                        <Typography variant="span" type="label" size={12} weight="700">
                            {priceValue}
                        </Typography>
                    </StyledChip>
                ) : (
                    '-'
                )}
            </StyledSearchListItemWrapper>
        </Link>
    );
}
